import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ListTodo,
  CheckCircle2,
  Circle,
  Calendar,
  Clock,
  Paperclip,
  Trash2,
  Search,
  Loader2,
  ArrowUpDown,
  ExternalLink,
  Building2,
  AlertTriangle,
  LayoutGrid,
} from 'lucide-react';
import { useLeads2Portal } from '../../contexts/Leads2PortalContext';
import { useToast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const TASK_TYPES = [
  { value: 'call', label: 'Chiamata', color: '#6366f1' },
  { value: 'email', label: 'Email', color: '#8b5cf6' },
  { value: 'meeting', label: 'Incontro', color: '#10b981' },
  { value: 'followup', label: 'Follow-up', color: '#f59e0b' },
  { value: 'other', label: 'Altro', color: '#64748b' },
];

const PRIORITIES = [
  { value: 'alta', label: 'Alta', color: '#ef4444' },
  { value: 'media', label: 'Media', color: '#f59e0b' },
  { value: 'bassa', label: 'Bassa', color: '#64748b' },
];

const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : null;
const fmtBytes = (n) => {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const dueGroup = (task) => {
  if (!task.due_at) return { key: 'nodate', label: 'Senza scadenza', tone: 'text-neutral-400' };
  const d = new Date(task.due_at);
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startDue = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((startDue - startToday) / 86400000);
  if (d < now) return { key: 'overdue', label: 'In ritardo', tone: 'text-red-600' };
  if (diffDays === 0) return { key: 'today', label: 'Oggi', tone: 'text-amber-600' };
  if (diffDays === 1) return { key: 'tomorrow', label: 'Domani', tone: 'text-fuchsia-600' };
  if (diffDays <= 7) return { key: 'week', label: 'Questa settimana', tone: 'text-neutral-600' };
  return { key: 'later', label: 'Più avanti', tone: 'text-neutral-500' };
};

const AdminLeads2Todo = () => {
  const navigate = useNavigate();
  const { token: adminToken, basePath, Layout } = useLeads2Portal();
  const { toast } = useToast();

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('todo');
  const [priority, setPriority] = useState('');
  const [type, setType] = useState('');
  const [sort, setSort] = useState('due');
  const [search, setSearch] = useState('');
  const [assignees, setAssignees] = useState([]);
  const [assigneeFilter, setAssigneeFilter] = useState('');

  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${adminToken}` }), [adminToken]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('status', status);
      if (priority) params.set('priority', priority);
      if (type) params.set('type', type);
      if (search.trim()) params.set('search', search.trim());
      params.set('sort', sort);
      const res = await fetch(`${API}/api/admin/leads2/tasks/all?${params.toString()}`, { headers: authHeaders() });
      if (res.ok) {
        const j = await res.json();
        setTasks(j.tasks || []);
      }
    } catch {
      toast({ title: 'Errore', description: 'Impossibile caricare le task', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [status, priority, type, sort, search, authHeaders, toast]);

  useEffect(() => { if (adminToken) load(); }, [adminToken, load]);

  useEffect(() => {
    if (!adminToken) return;
    fetch(`${API}/api/admin/leads2/assignees`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : { assignees: [] })
      .then((j) => setAssignees(j.assignees || []))
      .catch(() => {});
  }, [adminToken, authHeaders]);

  const visibleTasks = tasks.filter((t) => {
    if (!assigneeFilter) return true;
    const la = t.lead?.assigned_to || null;
    if (assigneeFilter === '__none__') return !la;
    return la === assigneeFilter;
  });

  const toggleStatus = async (t) => {
    await fetch(`${API}/api/admin/leads2/tasks/${t.id}`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: t.status === 'done' ? 'todo' : 'done' }),
    });
    load();
  };

  const uploadAttachment = async (taskId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API}/api/admin/leads2/tasks/${taskId}/attachments`, {
      method: 'POST', headers: authHeaders(), body: fd,
    });
    if (res.ok) { load(); toast({ title: 'Allegato caricato' }); }
    else { const e = await res.json().catch(() => ({})); toast({ title: 'Errore', description: e.detail || 'Upload fallito', variant: 'destructive' }); }
  };

  const deleteAttachment = async (taskId, attId) => {
    if (!window.confirm('Eliminare questo allegato?')) return;
    await fetch(`${API}/api/admin/leads2/tasks/${taskId}/attachments/${attId}`, { method: 'DELETE', headers: authHeaders() });
    load();
  };

  const counts = {
    overdue: visibleTasks.filter((t) => t.status !== 'done' && dueGroup(t).key === 'overdue').length,
    today: visibleTasks.filter((t) => t.status !== 'done' && dueGroup(t).key === 'today').length,
    alta: visibleTasks.filter((t) => t.status !== 'done' && (t.priority || 'media') === 'alta').length,
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="leads2-todo-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
              <ListTodo className="w-6 h-6 text-fuchsia-600"/> TO DO
            </h1>
            <p className="text-sm text-neutral-500 mt-0.5">Tutte le attività da svolgere, ordinate per scadenza e priorità.</p>
          </div>
          <button
            onClick={() => navigate(basePath)}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-2 border border-neutral-200 rounded-lg hover:bg-neutral-50 self-start"
            data-testid="goto-pipeline-btn"
          >
            <LayoutGrid className="w-4 h-4"/> Pipeline
          </button>
        </div>

        {/* KPI chips */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-red-50 border border-red-100 rounded-xl p-3" data-testid="kpi-overdue">
            <div className="text-xs text-red-500 font-medium flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5"/> In ritardo</div>
            <div className="text-2xl font-bold text-red-600">{counts.overdue}</div>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-3" data-testid="kpi-today">
            <div className="text-xs text-amber-600 font-medium flex items-center gap-1"><Clock className="w-3.5 h-3.5"/> Oggi</div>
            <div className="text-2xl font-bold text-amber-600">{counts.today}</div>
          </div>
          <div className="bg-fuchsia-50 border border-fuchsia-100 rounded-xl p-3" data-testid="kpi-alta">
            <div className="text-xs text-fuchsia-600 font-medium">Priorità alta</div>
            <div className="text-2xl font-bold text-fuchsia-600">{counts.alta}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-neutral-200 p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2"/>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per titolo o cliente…"
              className="w-full border border-neutral-200 rounded-lg pl-9 pr-3 py-2 text-sm"
              data-testid="todo-search"
            />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-neutral-200 rounded-lg px-3 py-2 text-sm" data-testid="todo-filter-status">
            <option value="todo">Da fare</option>
            <option value="done">Completate</option>
            <option value="all">Tutte</option>
          </select>
          <select value={priority} onChange={(e) => setPriority(e.target.value)} className="border border-neutral-200 rounded-lg px-3 py-2 text-sm" data-testid="todo-filter-priority">
            <option value="">Tutte le priorità</option>
            {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <select value={type} onChange={(e) => setType(e.target.value)} className="border border-neutral-200 rounded-lg px-3 py-2 text-sm" data-testid="todo-filter-type">
            <option value="">Tutti i tipi</option>
            {TASK_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <select value={assigneeFilter} onChange={(e) => setAssigneeFilter(e.target.value)} className="border border-neutral-200 rounded-lg px-3 py-2 text-sm" data-testid="todo-filter-assignee">
            <option value="">Tutti i gestori</option>
            <option value="__none__">Non assegnati</option>
            {assignees.map((a) => (
              <option key={a.id} value={a.id}>{a.name}{a.role !== 'admin' ? ` (${a.code || 'agente'})` : ' (admin)'}</option>
            ))}
          </select>
          <button
            onClick={() => setSort(sort === 'due' ? 'priority' : 'due')}
            className="inline-flex items-center gap-1.5 border border-neutral-200 rounded-lg px-3 py-2 text-sm hover:bg-neutral-50"
            data-testid="todo-sort-toggle"
          >
            <ArrowUpDown className="w-4 h-4"/> {sort === 'due' ? 'Per scadenza' : 'Per priorità'}
          </button>
        </div>

        {/* List */}
        {loading ? (
          <div className="p-10 text-center text-neutral-400"><Loader2 className="w-6 h-6 animate-spin mx-auto"/></div>
        ) : visibleTasks.length === 0 ? (
          <div className="bg-white rounded-2xl border border-neutral-200 p-12 text-center text-neutral-400">
            <ListTodo className="w-10 h-10 mx-auto mb-3 text-neutral-300"/>
            Nessuna task trovata con i filtri correnti.
          </div>
        ) : (
          <div className="space-y-2" data-testid="todo-list">
            {visibleTasks.map((t) => (
              <TodoRow
                key={t.id}
                task={t}
                onToggle={toggleStatus}
                onUpload={uploadAttachment}
                onDeleteAttachment={deleteAttachment}
                onOpenLead={() => t.lead && navigate(`${basePath}/${t.lead_id}`)}
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

const TodoRow = ({ task, onToggle, onUpload, onDeleteAttachment, onOpenLead }) => {
  const type = TASK_TYPES.find((t) => t.value === task.type) || TASK_TYPES[4];
  const prio = PRIORITIES.find((p) => p.value === (task.priority || 'media')) || PRIORITIES[1];
  const done = task.status === 'done';
  const grp = dueGroup(task);
  const fileRef = useRef(null);
  const attachments = task.attachments || [];
  const lead = task.lead;

  return (
    <div className={`bg-white rounded-xl border p-4 flex items-start gap-3 ${done ? 'opacity-60' : grp.key === 'overdue' ? 'border-red-200' : 'border-neutral-200'}`} data-testid={`todo-task-${task.id}`}>
      <button onClick={() => onToggle(task)} className="flex-shrink-0 mt-0.5" data-testid={`todo-toggle-${task.id}`}>
        {done ? <CheckCircle2 className="w-5 h-5 text-emerald-500"/> : <Circle className="w-5 h-5 text-neutral-300 hover:text-fuchsia-500"/>}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm font-semibold ${done ? 'line-through text-neutral-400' : 'text-neutral-900'}`}>{task.title}</span>
          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: prio.color + '15', color: prio.color }}>{prio.label}</span>
          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ backgroundColor: type.color + '15', color: type.color }}>{type.label}</span>
        </div>

        {/* Lead ref */}
        {lead ? (
          <div className="flex items-center gap-2 flex-wrap mt-1">
            <button onClick={onOpenLead} className="inline-flex items-center gap-1 text-xs text-fuchsia-700 hover:underline" data-testid={`todo-lead-${task.id}`}>
              <ExternalLink className="w-3 h-3"/>
              <span className="font-medium">{lead.full_name || lead.email || 'Lead'}</span>
              {lead.company && <span className="inline-flex items-center gap-0.5 text-neutral-500"><Building2 className="w-3 h-3"/> {lead.company}</span>}
            </button>
            {lead.assigned_to_name && (
              <span className="inline-flex items-center gap-1 text-[10px] text-neutral-500" title={`Gestito da ${lead.assigned_to_name}`} data-testid={`todo-assignee-${task.id}`}>
                <span className="w-4 h-4 rounded-full bg-fuchsia-600 text-white inline-flex items-center justify-center text-[8px] font-semibold">
                  {(lead.assigned_to_name.trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join('') || '?').toUpperCase()}
                </span>
                {lead.assigned_to_name}
              </span>
            )}
          </div>
        ) : (
          <div className="text-xs text-neutral-400 mt-1 italic">Lead rimosso</div>
        )}

        {task.description && <div className="text-xs text-neutral-500 mt-1 whitespace-pre-wrap">{task.description}</div>}

        <div className="flex items-center gap-3 mt-1.5 text-xs">
          <span className={`inline-flex items-center gap-1 font-medium ${grp.tone}`}>
            <Calendar className="w-3 h-3"/> {grp.label}{task.due_at ? ` · ${fmtDateTime(task.due_at)}` : ''}
          </span>
          {task.reminder_at && (
            <span className="inline-flex items-center gap-1 text-neutral-400">
              <Clock className="w-3 h-3"/> Reminder {fmtDateTime(task.reminder_at)}
            </span>
          )}
        </div>

        {attachments.length > 0 && (
          <div className="mt-2 space-y-1">
            {attachments.map((a) => (
              <div key={a.id} className="flex items-center gap-2 text-xs bg-neutral-50 border border-neutral-100 rounded px-2 py-1" data-testid={`todo-attachment-${a.id}`}>
                <Paperclip className="w-3 h-3 text-neutral-400 flex-shrink-0"/>
                <a href={a.url} target="_blank" rel="noopener noreferrer" className="text-fuchsia-700 hover:underline truncate flex-1">{a.original_name}</a>
                <span className="text-neutral-400 flex-shrink-0">{fmtBytes(a.size_bytes)}</span>
                <button onClick={() => onDeleteAttachment(task.id, a.id)} className="text-neutral-400 hover:text-red-500 flex-shrink-0" data-testid={`todo-attachment-delete-${a.id}`}>
                  <Trash2 className="w-3 h-3"/>
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-2">
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            onChange={(e) => { if (e.target.files?.[0]) { onUpload(task.id, e.target.files[0]); e.target.value = ''; } }}
            data-testid={`todo-attachment-input-${task.id}`}
          />
          <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1 text-[11px] text-fuchsia-600 hover:text-fuchsia-800" data-testid={`todo-attachment-add-${task.id}`}>
            <Paperclip className="w-3 h-3"/> Allega file
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminLeads2Todo;
